import { useState } from "react";
import { C, Card, SectionTitle, Btn, Badge } from "./theme";
import { POST } from "./api";
import { useLang } from "./i18n";

const TYPE_LABELS = { new_vm: "새 VM", resource: "리소스", gpu: "GPU" };
const TYPE_COLORS = { new_vm: C.cyan, resource: C.amber, gpu: C.green };
const STATUS_COLORS = { pending: C.amber, approved: C.green, rejected: C.red, "대기중": C.amber, "거절": C.red, "생성완료": C.green, "생성실패": C.red, "반납완료": C.dim2, "승인": C.green };

export default function AdminRequests(props) {
  const { requests, vms, fetchAll } = props;

  const getStatusLabel = (r) => r.display_status || r.status;
  const { t } = useLang();
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const filtered = (requests || []).filter(r => {
    if (filter === "pending") return r.status === "pending";
    return true;
  }).sort((a, b) => {
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (a.status !== "pending" && b.status === "pending") return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const pendingList = filtered.filter(r => r.status === "pending");
  const historyList = filtered.filter(r => r.status !== "pending");

  const handleAction = async (req, action) => {
    setBusy(req.id);
    try { await POST(`/api/requests/${req.id}/${action}`); fetchAll(); }
    catch (e) { alert(`${action} 실패: ` + (e.message || e)); }
    setBusy(null);
  };

  const fmtDate = (d) => d ? new Date(d+"Z").toLocaleString("ko-KR", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "-";

  const renderDetails = (r) => {
    if (r.type === "new_vm") return (
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, fontSize:11 }}>
        <span style={{color:C.dim2}}>{t("server")}: <strong style={{color:C.text}}>{r.server}</strong></span>
        <span style={{color:C.dim2}}>CPU: <strong style={{color:C.text}}>{r.cpu}</strong></span>
        <span style={{color:C.dim2}}>RAM: <strong style={{color:C.text}}>{r.ram}G</strong></span>
        <span style={{color:C.dim2}}>Disk: <strong style={{color:C.text}}>{r.disk}G</strong></span>
        {r.gpu_count > 0 && <span style={{color:C.dim2}}>GPU: <strong style={{color:C.text}}>{r.gpu_count}</strong></span>}
      </div>
    );
    if (r.type === "resource") return (
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, fontSize:11 }}>
        <span style={{color:C.dim2}}>VM: <strong style={{color:C.cyan}}>{r.vm_id}</strong></span>
        {r.cpu !== 0 && <span style={{color:C.dim2}}>CPU: <strong style={{color:C.text}}>{r.cpu>0?"+":""}{r.cpu}</strong></span>}
        {r.ram !== 0 && <span style={{color:C.dim2}}>RAM: <strong style={{color:C.text}}>{r.ram>0?"+":""}{r.ram}G</strong></span>}
      </div>
    );
    if (r.type === "gpu") return (
      <div style={{ fontSize:11 }}>
        <span style={{color:C.dim2}}>VM: <strong style={{color:C.cyan}}>{r.vm_id}</strong></span>
        {" "}<span style={{color:C.dim2}}>GPU: <strong style={{color:C.text}}>{r.gpu_count}개</strong></span>
      </div>
    );
    return null;
  };

  const renderCard = (r, showActions) => (
    <Card key={r.id} style={{ padding:14, marginBottom:8 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <Badge label={TYPE_LABELS[r.type]||r.type} color={TYPE_COLORS[r.type]||C.dim2}/>
            <span style={{ color:C.text, fontWeight:700, fontSize:12 }}>{r.user_id}</span>
            <span style={{ color:C.dim2, fontSize:10 }}>{fmtDate(r.created_at)}</span>
            {!showActions && <Badge label={getStatusLabel(r)} color={STATUS_COLORS[getStatusLabel(r)]||C.dim2}/>}
          </div>
          {renderDetails(r)}
          {r.reason && (
            <div style={{ marginTop:6, padding:"4px 8px", background:C.bg, borderRadius:4,
              borderLeft:`3px solid ${C.cyan}`, fontSize:11, color:C.text }}>{r.reason}</div>
          )}
          {r.status !== "pending" && (
            <div style={{ marginTop:4, fontSize:10, color:C.dim2 }}>
              {r.reviewed_by
                ? <>{r.status === "rejected" ? "거절" : "승인"}: <span style={{color:C.text}}>{r.reviewed_by}</span> ({fmtDate(r.reviewed_at)})</>
                : r.reviewed_at
                ? <>자동 승인 ({fmtDate(r.reviewed_at)})</>
                : null}
              {r.vm_id && <> · VM: <span style={{color:C.cyan}}>{r.vm_id}</span></>}
            </div>
          )}
        </div>
        {showActions && (
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            <Btn small variant="green" disabled={busy===r.id} onClick={()=>handleAction(r,"approve")}>
              {busy===r.id?"...":t("approve")}
            </Btn>
            <Btn small variant="red" disabled={busy===r.id} onClick={()=>handleAction(r,"reject")}>
              {busy===r.id?"...":t("reject")}
            </Btn>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div>
      <SectionTitle title={t("request_management")} action={
        <div style={{display:"flex",gap:6}}>
          <Btn small variant={filter==="pending"?"amber":"ghost"} onClick={()=>setFilter("pending")}>
            {t("waiting")} ({(requests||[]).filter(r=>r.status==="pending").length})
          </Btn>
          <Btn small variant={filter==="all"?"blue":"ghost"} onClick={()=>setFilter("all")}>{t("all")}</Btn>
        </div>
      }/>

      {pendingList.length > 0 && (
        <>
          <div style={{color:C.amber,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:1}}>
            {t("waiting")} ({pendingList.length})
          </div>
          {pendingList.map(r => renderCard(r, true))}
        </>
      )}

      {pendingList.length === 0 && filter === "pending" && (
        <Card style={{textAlign:"center",color:C.dim2,padding:32}}>{t("no_pending")}</Card>
      )}

      {historyList.length > 0 && (
        <>
          <div style={{color:C.dim2,fontSize:11,fontWeight:700,marginBottom:8,marginTop:20,letterSpacing:1}}>
            {t("processed_history")}
          </div>
          {historyList.map(r => renderCard(r, false))}
        </>
      )}
    </div>
  );
}
